import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, ArrowRight } from "lucide-react";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Hoş geldiniz");
      nav(u?.role === "accountant" ? "/islemler" : "/");
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen fk-grain flex" data-testid="login-page">
      {/* Left — brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] p-12 relative overflow-hidden"
           style={{ background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%)" }}>
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(99,102,241,0.18)", border: "1px solid rgba(99,102,241,0.35)" }}>
            <Building2 className="w-5 h-5 text-indigo-300" />
          </div>
          <div>
            <div className="text-white font-semibold tracking-tight text-lg fk-heading">Yapı Denetim</div>
            <div className="text-xs text-slate-400">Çoklu Firma Finans Yönetimi</div>
          </div>
        </div>

        <div className="relative z-10">
          <div className="text-xs font-mono text-indigo-300 mb-3 uppercase tracking-widest">5 Firma · 3 Ortak · 10 Ortak</div>
          <h1 className="text-4xl xl:text-5xl font-bold text-white tracking-tight leading-tight fk-heading">
            Ortak giderler <span className="text-indigo-400">%20 eşit</span>,<br />
            kârlar şeffaf dağıtılır.
          </h1>
          <p className="mt-6 text-slate-400 text-base leading-relaxed max-w-md">
            Firmaların gelir ve giderlerini aylık olarak takip edin. 10 ortak giderini
            otomatik olarak 5 firmaya eşit paylaştırın, ortak kârını anında hesaplayın.
          </p>
        </div>

        <div className="relative z-10 text-xs text-slate-500 font-mono">
          © {new Date().getFullYear()} Yapı Denetim · v1.0
        </div>

        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{
               backgroundImage:
                 "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
               backgroundSize: "48px 48px",
             }}/>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <div className="mb-10">
            <div className="text-xs font-mono text-slate-500 mb-2 uppercase tracking-widest">Oturum Aç</div>
            <h2 className="text-3xl font-bold tracking-tight fk-heading">Panele Giriş</h2>
            <p className="text-slate-400 mt-2 text-sm">Kullanıcı adı ve şifrenizle devam edin.</p>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Kullanıcı Adı</Label>
              <Input
                id="email" type="text" required autoComplete="username"
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-slate-900/60 border-slate-700 focus-visible:ring-indigo-500"
                placeholder="yönetim veya muhasebe"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Şifre</Label>
              <Input
                id="password" type="password" required
                data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-11 bg-slate-900/60 border-slate-700 focus-visible:ring-indigo-500"
                placeholder="••••••••"
              />
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit" disabled={loading}
              className="w-full h-11 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold group"
            >
              {loading ? "Giriş yapılıyor..." : (
                <span className="inline-flex items-center gap-2">
                  Giriş Yap <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5"/>
                </span>
              )}
            </Button>
          </div>

          <div className="mt-10 p-4 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-slate-400 space-y-1">
            <div className="font-mono text-slate-500 mb-1">Demo Girişler</div>
            <div><span className="text-indigo-300 font-medium">yönetim</span> / istanbul · tam erişim</div>
            <div><span className="text-emerald-300 font-medium">muhasebe</span> / yapı denetim · sadece Gelir &amp; Gider</div>
          </div>
        </form>
      </div>
    </div>
  );
}
